import { formatCurrency } from '@utils/common';
import {
  ChartEvent,
  ChartOptions,
  ChartType,
  LegendElement,
  Plugin,
  type ScriptableScaleContext,
} from 'chart.js';
import { CustomChartFilters } from './types';
import { Chart } from 'chart.js';
import { LegendItem } from 'chart.js';
import ChartAnnotation from 'chartjs-plugin-annotation';
import i18n from '@utils/i18next';

// Register the annotation plugin
Chart.register(ChartAnnotation);

export interface TooltipBodyConfig {
  type: 'indicator' | 'label' | 'value' | 'void' | 'text' | 'break' | 'text-value';
  text?: string;
  transformer?: AxisTransformer;
  value?: number | string | { [key: string]: number | string };
  key?: string;
  backgroundColor?: string;
}

export interface TooltipConfig {
  title?: string;
  bodyConfigs: TooltipBodyConfig[];
  footerConfigs?: TooltipBodyConfig[];
}

/* Types */
interface DefaultChartPluginsProps {
  xLabel?: string;
  yLabel?: string;
  y1Label?: string;
}

interface DefaultChartOptionsProps {
  xAxisTransformationFuntion?: AxisTransformer;
  yAxisTransformationFuntion?: AxisTransformer;
  y1AxisTransformationFuntion?: AxisTransformer;
  showXGrid?: boolean;
  showYGrid?: boolean;
  isStacked?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  beginXAtValue?: number;
  beginYAtValue?: number;
  y1Axis?: boolean;
  yAxis?: boolean;
  xAxisTicksCallback?: (value: any, index: number) => string;
  customTooltipContent?: (context: any) => {
    tooltipData?: TooltipData[];
    footerContent?: Omit<TooltipData, 'backgroundColor'>;
  };
  tooltipConfigs?: TooltipConfig;
  xAxisLabel?: string;
  threshold?: number;
  containerId?: string;
  showCustomFilters?: boolean;
  hideXTicks?: boolean;
  xTickFontSize?: number;
  bottomPadding?: number;
  fixedYAxisWidth?: number;
  gridBorderColor?: string;
  gridBorderStyle?: 'dashed' | 'solid';
  devicePixelRatio?: number;
  showXAxisBorder?: boolean;
  zeroLineColor?: string;
}

/* Utils */
export const initiateCustomChartTooltip = (
  context: any,
  containerId: string,
): { tooltipEl: HTMLElement; tooltipModel: any } => {
  let tooltipEl = document.getElementById('chart-tooltip') as HTMLElement | null;

  const container = document.getElementById(containerId);

  if (container) {
    container.addEventListener('scroll', () => {
      if (tooltipEl) {
        tooltipEl.style.opacity = '0';
      }
    });
  }

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chart-tooltip';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.background = '#fff';
    tooltipEl.style.color = '#162C36';
    tooltipEl.style.border = '1px solid #737F86';
    tooltipEl.style.borderRadius = '0.5rem';
    tooltipEl.style.padding = '8px';
    tooltipEl.style.boxShadow = '0px 4px 6px rgba(0,0,0,0.1)';
    tooltipEl.style.fontFamily = 'Manrope, sans-serif';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.whiteSpace = 'nowrap';
    tooltipEl.style.opacity = '0';
    document.body.appendChild(tooltipEl);
  }

  const tooltipModel = context.tooltip;
  if (!tooltipModel || tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return { tooltipEl, tooltipModel };
  }

  const chartCanvas = context.chart.canvas;
  const position = chartCanvas.getBoundingClientRect();
  const tooltipWidth = tooltipEl.offsetWidth;
  const tooltipHeight = tooltipEl.offsetHeight;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  let tooltipX = position.left + window.pageXOffset + tooltipModel.caretX;
  let tooltipY = position.top + window.pageYOffset + tooltipModel.caretY;

  if (tooltipX + tooltipWidth > screenWidth) {
    tooltipX -= tooltipWidth + 20;
  }
  if (tooltipX < 0) {
    tooltipX = 10;
  }
  if (tooltipY + tooltipHeight > screenHeight) {
    tooltipY -= tooltipHeight + 20;
  }
  if (tooltipY < 0) {
    tooltipY = 10;
  }

  tooltipEl.style.left = `${tooltipX}px`;
  tooltipEl.style.top = `${tooltipY}px`;
  tooltipEl.style.opacity = '1';
  tooltipEl.style.zIndex = '2000';

  return { tooltipEl, tooltipModel };
};

export type TooltipData = {
  label: string;
  value: number | string;
  rawValue?: any;
  style?: string;
  backgroundColor?: string;
};

export type CustomChartTooltipHandlerProps = {
  context: any;
  containerId: string;
  title: string;
  tooltipData?: TooltipData[];
  footerContent?: Omit<TooltipData, 'backgroundColor'>;
  customTooltipContent?: string;
  tooltipConfigs: TooltipConfig;
  yAxisTransformer: any;
  xAxisTransformer: any;
  xAxisLabel: string;
};

export const customChartTooltipHandler = (props: CustomChartTooltipHandlerProps) => {
  const { context, containerId, tooltipData } = props;
  const { tooltipEl, tooltipModel } = initiateCustomChartTooltip(context, containerId);

  const title = tooltipModel.title ? tooltipModel.title[0] : '';

  const cellStyling =
    'padding: 4px 8px; white-space: nowrap; font-size: 0.75rem; color: #6C6C6C; font-weight: 600;';

  const header = props.xAxisLabel
    ? `${props.xAxisLabel} (${props.xAxisTransformer(title)})`
    : title;
  let content = `<div style="font-weight: bold; margin-bottom: 4px;">${header}</div>`;

  content += `<table>
    <tbody>
    `;

  for (const data of tooltipData || []) {
    content += `
      <tr>
      `;

    for (const config of props.tooltipConfigs?.bodyConfigs || []) {
      if (config.type === 'indicator') {
        content += `
          <td>
            <div style="background-color: ${data.backgroundColor}; width: 10px; height: 10px; border-radius: 50%;"></div>
          </td>
          `;
      }

      if (config.type === 'value') {
        let value = data.value;
        if (!isNaN(+data.value)) {
          value = (+data.value).toFixed(2);
        }
        content += `
          <td style="${cellStyling}">${value}</td>
          `;
      } else if (config.type === 'label') {
        content += `
          <td style="${cellStyling}">${data.label}</td>
          `;
      } else if (config.type === 'break') {
        content += `
          </tr>
          <tr>
          `;
      } else if (config.type === 'text-value') {
        const item = (data.rawValue as any)[config.key as string];
        const value = config.transformer ? AXIS_TRANSFORMERS[config.transformer](item) : item;
        content += `<td style="${cellStyling}">${value}</td>`;
      } else if (config.type === 'text') {
        content += `<td style="${cellStyling}">${config.text}</td>`;
      }
    }

    content += `</tr>`;
  }

  content += `
    <tr>
    `;

  for (const config of props.tooltipConfigs?.footerConfigs || []) {
    if (config.type === 'indicator') {
      content += `
        <td>
          <div style="background-color: ${config.backgroundColor}; width: 10px; height: 10px; border-radius: 50%;"></div>
        </td>
        `;
    } else if (config.type === 'text') {
      content += `<td style="${cellStyling}">${config.text}</td>`;
    } else if (config.type === 'text-value') {
      const item = (config.value as any)[config.key as string];
      const value = config.transformer ? AXIS_TRANSFORMERS[config.transformer](item) : item;
      content += `<td style="${cellStyling}">${value}</td>`;
    }
  }

  content += `</tr>`;

  content += `</tbody></table>`;

  tooltipEl.style.width = 'auto';
  tooltipEl.style.height = 'auto';
  tooltipEl.style.padding = '12px';
  tooltipEl.style.background = '#fff';
  tooltipEl.style.borderRadius = '8px';

  tooltipEl.innerHTML = content;
};

// Helper function to get translated filter label
export const getFilterLabel = (filter: CustomChartFilters): string => {
  switch (filter) {
    case CustomChartFilters.SELECT_ALL:
      return i18n.t('gettingStarted.charts.filters.select_all');
    case CustomChartFilters.INVERSE:
      return i18n.t('gettingStarted.charts.filters.inverse');
    default:
      return filter;
  }
};

// Helper function to get filter enum from translated text
export const getFilterFromLabel = (label: string): CustomChartFilters | null => {
  const selectAllLabel = i18n.t('gettingStarted.charts.filters.select_all');
  const inverseLabel = i18n.t('gettingStarted.charts.filters.inverse');

  if (label === selectAllLabel) return CustomChartFilters.SELECT_ALL;
  if (label === inverseLabel) return CustomChartFilters.INVERSE;

  // Fallback to enum values for backward compatibility
  if (label === CustomChartFilters.SELECT_ALL) return CustomChartFilters.SELECT_ALL;
  if (label === CustomChartFilters.INVERSE) return CustomChartFilters.INVERSE;

  return null;
};

export const getCustomFilterAction = (filter: CustomChartFilters, chart: Chart) => {
  const items = chart.options.plugins?.legend?.labels?.generateLabels?.(chart);
  switch (filter) {
    case CustomChartFilters.SELECT_ALL:
      return items?.forEach((item) => {
        chart.setDatasetVisibility(item.datasetIndex as number, true);
      });
    case CustomChartFilters.INVERSE:
      return items?.forEach((item) => {
        chart.setDatasetVisibility(
          item.datasetIndex as number,
          !chart.isDatasetVisible(item.datasetIndex as number),
        );
      });
    default:
      return;
  }
};

const getCustomLegendFilterPlugin = (showCustomFilters: boolean = true) => {
  const generateLabels = (chart: Chart) => {
    const defaultLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);

    const customFilterLabels: LegendItem[] = showCustomFilters
      ? Object.values(CustomChartFilters).map((filter, i) => ({
          text: getFilterLabel(filter),
          fontColor: '#51B8B0',
          fontWeight: 700,
          hidden: false,
          index: defaultLabels.length + i,
          lineWidth: 0,
          fillStyle: 'transparent',
        }))
      : [];

    return [...defaultLabels, ...customFilterLabels];
  };

  const onClick = function (
    this: LegendElement<ChartType>,
    e: ChartEvent,
    legendItem: LegendItem,
    legend: LegendElement<ChartType>,
  ) {
    const chart = legend.chart;

    if (legendItem.datasetIndex != null) {
      const meta = chart.getDatasetMeta(legendItem.datasetIndex);
      const currentHidden =
        meta.hidden === null
          ? (chart.data.datasets[legendItem.datasetIndex].hidden ?? false)
          : meta.hidden;
      meta.hidden = !currentHidden;
    } else {
      const filter = getFilterFromLabel(legendItem.text);
      if (filter) {
        getCustomFilterAction(filter, chart);
      }
    }
    chart.update();
  };

  return {
    labels: {
      // General
      padding: 10,
      generateLabels,
      color: '#6C6C6C',
      font: {
        family: 'Manrope',
        size: 12,
        weight: 600,
        lineHeight: 1.2,
      },
      // For box style
      boxWidth: 8,
      boxHeight: 8,
      useBorderRadius: true,
      borderRadius: 4,
      // For point style
      usePointStyle: true,
      pointStyle: 'circle',
    },
    onClick,
  };
};

const customXandYAxisLabelPlugin = (xLabel: string, yLabel: string, y1Label?: string) => ({
  id: 'customXAxisLabelPlugin',
  afterDraw: (chart: any) => {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;

    const padding = 10;
    const borderRadius = 10;
    const arrowSize = 8;
    const arrowTailLength = 8;

    // === X-Axis Label Drawing ===
    if (xLabel && xLabel.trim() !== '') {
      const label = xLabel;
      const labelX = (xAxis.left + xAxis.right) / 2;
      const labelY = xAxis.bottom - 20;

      ctx.font = '700 12px Manrope';
      const textWidth = ctx.measureText(label).width;
      const labelWidth = textWidth + padding * 2;
      const labelHeight = 20;

      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = '#E8EAEB';
      ctx.lineWidth = 1;

      ctx.beginPath();
      ctx.moveTo(labelX - labelWidth / 2 + borderRadius, labelY - labelHeight / 2);
      ctx.lineTo(labelX + labelWidth / 2 - borderRadius + 25, labelY - labelHeight / 2);
      ctx.quadraticCurveTo(
        labelX + labelWidth / 2 + 25,
        labelY - labelHeight / 2,
        labelX + labelWidth / 2 + 25,
        labelY - labelHeight / 2 + borderRadius,
      );
      ctx.lineTo(labelX + labelWidth / 2 + 25, labelY + labelHeight / 2 - borderRadius);
      ctx.quadraticCurveTo(
        labelX + labelWidth / 2 + 25,
        labelY + labelHeight / 2,
        labelX + labelWidth / 2 - borderRadius + 25,
        labelY + labelHeight / 2,
      );
      ctx.lineTo(labelX - labelWidth / 2 + borderRadius, labelY + labelHeight / 2);
      ctx.quadraticCurveTo(
        labelX - labelWidth / 2,
        labelY + labelHeight / 2,
        labelX - labelWidth / 2,
        labelY + labelHeight / 2 - borderRadius,
      );
      ctx.lineTo(labelX - labelWidth / 2, labelY - labelHeight / 2 + borderRadius);
      ctx.quadraticCurveTo(
        labelX - labelWidth / 2,
        labelY - labelHeight / 2,
        labelX - labelWidth / 2 + borderRadius,
        labelY - labelHeight / 2,
      );
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#162C36';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, labelX, labelY + 1);

      // Arrow (X)
      const arrowX = labelX + labelWidth / 2 + 5;
      const arrowY = labelY - 1;
      ctx.beginPath();
      ctx.moveTo(arrowX - arrowTailLength, arrowY);
      ctx.lineTo(arrowX, arrowY);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY - arrowSize / 2);
      ctx.lineTo(arrowX + arrowSize, arrowY);
      ctx.lineTo(arrowX, arrowY + arrowSize / 2);
      ctx.closePath();
      ctx.fillStyle = '#000';
      ctx.fill();
    }

    // === Y-Axis Label Drawing ===
    if (yLabel && yLabel.trim() !== '') {
      const yAxis = chart.scales.y;
      const ctxY = chart.ctx;
      const labelText = yLabel;

      const textMetrics = ctxY.measureText(labelText);
      const labelBoxWidth = textMetrics.width + 20;
      const labelBoxHeight = 20;

      const labelCenterX = yAxis.left - 40;
      const labelCenterY = (yAxis.top + yAxis.bottom) / 2;

      ctxY.save();
      ctxY.translate(labelCenterX, labelCenterY);
      ctxY.rotate(-Math.PI / 2);

      ctxY.beginPath();
      ctxY.moveTo(-labelBoxWidth / 2 + borderRadius, -labelBoxHeight / 2);
      ctxY.lineTo(labelBoxWidth / 2 - borderRadius, -labelBoxHeight / 2);
      ctxY.quadraticCurveTo(
        labelBoxWidth / 2,
        -labelBoxHeight / 2,
        labelBoxWidth / 2,
        -labelBoxHeight / 2 + borderRadius,
      );
      ctxY.lineTo(labelBoxWidth / 2, labelBoxHeight / 2 - borderRadius);
      ctxY.quadraticCurveTo(
        labelBoxWidth / 2,
        labelBoxHeight / 2,
        labelBoxWidth / 2 - borderRadius,
        labelBoxHeight / 2,
      );
      ctxY.lineTo(-labelBoxWidth / 2 + borderRadius, labelBoxHeight / 2);
      ctxY.quadraticCurveTo(
        -labelBoxWidth / 2,
        labelBoxHeight / 2,
        -labelBoxWidth / 2,
        labelBoxHeight / 2 - borderRadius,
      );
      ctxY.lineTo(-labelBoxWidth / 2, -labelBoxHeight / 2 + borderRadius);
      ctxY.quadraticCurveTo(
        -labelBoxWidth / 2,
        -labelBoxHeight / 2,
        -labelBoxWidth / 2 + borderRadius,
        -labelBoxHeight / 2,
      );
      ctxY.closePath();

      ctxY.fillStyle = '#FFFFFF';
      ctxY.strokeStyle = '#E8EAEB';
      ctxY.lineWidth = 1;
      ctxY.fill();
      ctxY.stroke();

      ctxY.fillStyle = '#162C36';
      ctxY.textAlign = 'center';
      ctxY.textBaseline = 'middle';
      ctxY.fillText(labelText, 0, 1);

      const arrowBaseX = labelBoxWidth / 2 + 6;
      const arrowBaseY = 0;

      ctxY.beginPath();
      ctxY.moveTo(arrowBaseX, arrowBaseY);
      ctxY.lineTo(arrowBaseX + arrowTailLength, arrowBaseY);
      ctxY.strokeStyle = '#000';
      ctxY.lineWidth = 2;
      ctxY.stroke();
      ctxY.beginPath();
      ctxY.moveTo(arrowBaseX + arrowTailLength, -arrowSize / 2);
      ctxY.lineTo(arrowBaseX + arrowTailLength + arrowSize, 0);
      ctxY.lineTo(arrowBaseX + arrowTailLength, arrowSize / 2);
      ctxY.closePath();
      ctxY.fillStyle = '#000';
      ctxY.fill();

      ctxY.restore();
    }

    // === Y1-Axis Label Drawing (if y1Label is provided) ===
    if (y1Label && chart.scales.y1) {
      const y1Axis = chart.scales.y1;
      const ctxY1 = chart.ctx;
      const y1LabelText = y1Label;

      const y1TextMetrics = ctxY1.measureText(y1LabelText);
      const y1LabelBoxWidth = y1TextMetrics.width + 20;
      const y1LabelBoxHeight = 20;

      const y1LabelCenterX = y1Axis.right + 40;
      const y1LabelCenterY = (y1Axis.top + y1Axis.bottom) / 2;

      ctxY1.save();
      ctxY1.translate(y1LabelCenterX, y1LabelCenterY);
      ctxY1.rotate(-Math.PI / 2);

      ctxY1.beginPath();
      ctxY1.moveTo(-y1LabelBoxWidth / 2 + borderRadius, -y1LabelBoxHeight / 2);
      ctxY1.lineTo(y1LabelBoxWidth / 2 - borderRadius, -y1LabelBoxHeight / 2);
      ctxY1.quadraticCurveTo(
        y1LabelBoxWidth / 2,
        -y1LabelBoxHeight / 2,
        y1LabelBoxWidth / 2,
        -y1LabelBoxHeight / 2 + borderRadius,
      );
      ctxY1.lineTo(y1LabelBoxWidth / 2, y1LabelBoxHeight / 2 - borderRadius);
      ctxY1.quadraticCurveTo(
        y1LabelBoxWidth / 2,
        y1LabelBoxHeight / 2,
        y1LabelBoxWidth / 2 - borderRadius,
        y1LabelBoxHeight / 2,
      );
      ctxY1.lineTo(-y1LabelBoxWidth / 2 + borderRadius, y1LabelBoxHeight / 2);
      ctxY1.quadraticCurveTo(
        -y1LabelBoxWidth / 2,
        y1LabelBoxHeight / 2,
        -y1LabelBoxWidth / 2,
        y1LabelBoxHeight / 2 - borderRadius,
      );
      ctxY1.lineTo(-y1LabelBoxWidth / 2, -y1LabelBoxHeight / 2 + borderRadius);
      ctxY1.quadraticCurveTo(
        -y1LabelBoxWidth / 2,
        -y1LabelBoxHeight / 2,
        -y1LabelBoxWidth / 2 + borderRadius,
        -y1LabelBoxHeight / 2,
      );
      ctxY1.closePath();

      ctxY1.fillStyle = '#FFFFFF';
      ctxY1.strokeStyle = '#E8EAEB';
      ctxY1.lineWidth = 1;
      ctxY1.fill();
      ctxY1.stroke();

      ctxY1.fillStyle = '#162C36';
      ctxY1.textAlign = 'center';
      ctxY1.textBaseline = 'middle';
      ctxY1.fillText(y1LabelText, 0, 1);

      // Arrow for Y1 axis (pointing upward)
      const y1ArrowBaseX = y1LabelBoxWidth / 2 + 6;
      const y1ArrowBaseY = 0;

      ctxY1.beginPath();
      ctxY1.moveTo(y1ArrowBaseX, y1ArrowBaseY);
      ctxY1.lineTo(y1ArrowBaseX + arrowTailLength, y1ArrowBaseY);
      ctxY1.strokeStyle = '#000';
      ctxY1.lineWidth = 2;
      ctxY1.stroke();
      ctxY1.beginPath();
      ctxY1.moveTo(y1ArrowBaseX + arrowTailLength, -arrowSize / 2);
      ctxY1.lineTo(y1ArrowBaseX + arrowTailLength + arrowSize, 0);
      ctxY1.lineTo(y1ArrowBaseX + arrowTailLength, arrowSize / 2);
      ctxY1.closePath();
      ctxY1.fillStyle = '#000';
      ctxY1.fill();

      ctxY1.restore();
    }
  },
});

export enum AxisTransformer {
  NONE = 'none',
  PERCENTAGE = 'percentage',
  PERCENTAGE_INVESTOR_REPORT = 'percentageInvestorReport',
  CURRENCY = 'currency',
  CURRENCY_INVESTOR_REPORT = 'currencyInvestorReport',
  CURRENCY_IN_MILLIONS = 'currencyInMillions',
  NUMBER_ABBREVIATION = 'numberAbbreviation',
}

// formatCurrency only treats positive values as millions, so format the magnitude and re-apply the sign
export const formatSignedAbbreviatedCurrency = (value: number | string) => {
  const numericValue = Number(value);
  if (numericValue < 0) return `-${formatCurrency(Math.abs(numericValue), true)}`;
  return formatCurrency(numericValue, true);
};

export const AXIS_TRANSFORMERS: Record<
  AxisTransformer,
  (value: number | string) => string | number
> = {
  [AxisTransformer.NONE]: (value: number | string) => (value ? value.toString() : ''),
  [AxisTransformer.PERCENTAGE]: (value: number | string) => `${(Number(value) * 100).toFixed(2)}%`,
  [AxisTransformer.PERCENTAGE_INVESTOR_REPORT]: (value: number | string) =>
    `${(Number(value) * 100).toFixed(0)}`,
  [AxisTransformer.CURRENCY]: (value: number | string) => `${formatCurrency(value, true)}`,
  [AxisTransformer.CURRENCY_INVESTOR_REPORT]: formatSignedAbbreviatedCurrency,
  [AxisTransformer.CURRENCY_IN_MILLIONS]: (value: number | string) =>
    `${formatCurrency(value, true, 1)}`,
  [AxisTransformer.NUMBER_ABBREVIATION]: (value: number | string) => {
    const num = Number(value);
    if (isNaN(num)) return value.toString();
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(num);
  },
};

export const TOOLTIP_TRANSFORMERS: Record<
  AxisTransformer,
  (value: number | string) => string | number
> = {
  [AxisTransformer.NONE]: (value: number | string) => (value ? value.toString() : ''),
  [AxisTransformer.PERCENTAGE]: (value: number | string) => `${(Number(value) * 100).toFixed(2)}%`,
  [AxisTransformer.PERCENTAGE_INVESTOR_REPORT]: (value: number | string) =>
    `${(Number(value) * 100).toFixed(2)}%`,
  [AxisTransformer.CURRENCY]: (value: number | string) => `${formatCurrency(value, false, 0, 0)}`,
  [AxisTransformer.CURRENCY_INVESTOR_REPORT]: (value: number | string) =>
    `${formatCurrency(value, false, 0, 0)}`,
  [AxisTransformer.CURRENCY_IN_MILLIONS]: (value: number | string) =>
    `${formatCurrency(value, false, 0, 0)}`,
  [AxisTransformer.NUMBER_ABBREVIATION]: (value: number | string) => {
    const num = Number(value);
    if (isNaN(num)) return value.toString();
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(num);
  },
};

export const getDefaultChartOptions = (args: DefaultChartOptionsProps = {}): ChartOptions => {
  const props: {
    xAxisLabel: string;
    xAxisTransformationFuntion: AxisTransformer;
    yAxisTransformationFuntion: AxisTransformer;
    y1AxisTransformationFuntion: AxisTransformer;
    showXGrid: boolean;
    showYGrid: boolean;
    isStacked?: boolean;
    showLegend?: boolean;
    showTooltip?: boolean;
    customTooltipContent?: (context: any) => {
      tooltipData?: TooltipData[];
      footerContent?: Omit<TooltipData, 'backgroundColor'>;
    };
    beginXAtValue?: number;
    beginYAtValue?: number;
    y1Axis?: boolean;
    yAxis?: boolean;
    xAxisTicksCallback?: (value: any, index: number) => string;
    tooltipConfigs: TooltipConfig;
    threshold?: number;
    containerId?: string;
    showCustomFilters?: boolean;
    hideXTicks?: boolean;
    xTickFontSize?: number;
    bottomPadding?: number;
    fixedYAxisWidth?: number;
    gridBorderStyle?: 'dashed' | 'solid';
    gridBorderColor?: string;
    devicePixelRatio?: number;
    showXAxisBorder: boolean;
    zeroLineColor?: string;
  } = {
    xAxisTransformationFuntion: AxisTransformer.NONE,
    yAxisTransformationFuntion: AxisTransformer.NONE,
    y1AxisTransformationFuntion: AxisTransformer.NONE,
    showXGrid: true,
    showYGrid: true,
    gridBorderStyle: 'dashed',
    gridBorderColor: '#E8EAEB',
    isStacked: false,
    showLegend: true,
    y1Axis: false,
    yAxis: true,
    xAxisLabel: '',
    showCustomFilters: true,
    showTooltip: true,
    tooltipConfigs: {
      bodyConfigs: [],
    },
    hideXTicks: false,
    xTickFontSize: 12,
    bottomPadding: 20,
    fixedYAxisWidth: 0,
    devicePixelRatio: 1,
    showXAxisBorder: true,
    ...args,
  };

  const xAxisTransformer = AXIS_TRANSFORMERS[props.xAxisTransformationFuntion]
    ? AXIS_TRANSFORMERS[props.xAxisTransformationFuntion]
    : (value: number | string) => value;
  const yAxisTransformer = AXIS_TRANSFORMERS[props.yAxisTransformationFuntion]
    ? AXIS_TRANSFORMERS[props.yAxisTransformationFuntion]
    : (value: number | string) => value;
  const y1AxisTransformer = AXIS_TRANSFORMERS[props.y1AxisTransformationFuntion]
    ? AXIS_TRANSFORMERS[props.y1AxisTransformationFuntion]
    : (value: number | string) => value;
  const tooltipTransformer = TOOLTIP_TRANSFORMERS[props.yAxisTransformationFuntion]
    ? TOOLTIP_TRANSFORMERS[props.yAxisTransformationFuntion]
    : (value: number | string) => value;

  return {
    devicePixelRatio: props.devicePixelRatio,
    plugins: {
      legend: {
        display: props.showLegend,
        position: 'bottom',
        align: 'start',
        ...getCustomLegendFilterPlugin(props.showCustomFilters),
      },

      tooltip: {
        enabled: false,
        ...(props.showTooltip
          ? {
              external: (context) => {
                const chartData = context.chart.data.datasets;
                if (!context.tooltip.dataPoints || context.tooltip.dataPoints.length === 0) return;
                const dataIndex = context.tooltip.dataPoints[0].dataIndex;

                const { tooltipData: customTooltipData, footerContent: customFooterContent } =
                  props?.customTooltipContent?.(context) ?? {};
                const tooltipData: TooltipData[] =
                  customTooltipData ??
                  chartData
                    .filter((dataset: any) => !!dataset.data[dataIndex])
                    .map((dataset: any) => {
                      return {
                        backgroundColor: dataset.backgroundColor,
                        label: dataset.label,
                        value: `${tooltipTransformer(dataset.data[dataIndex])}`,
                        rawValue: dataset.data[dataIndex],
                        style: 'font-weight: 600;',
                      };
                    });
                customChartTooltipHandler({
                  context,
                  title: `${context.tooltip.title[0]}`,
                  containerId: props.containerId ?? `pvx-underwriting-container`,
                  tooltipData,
                  footerContent: customFooterContent,
                  tooltipConfigs: props.tooltipConfigs,
                  yAxisTransformer: yAxisTransformer,
                  xAxisTransformer: xAxisTransformer,
                  xAxisLabel: props.xAxisLabel,
                });
              },
            }
          : {}),
      },

      annotation: {
        annotations: props.threshold
          ? {
              thresholdLine: {
                type: 'line',
                yMin: props.threshold,
                yMax: props.threshold,
                borderColor: '#162C36',
                borderWidth: 2,
                borderDash: [5, 5],
              },
            }
          : {},
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    layout: {
      padding: {
        left: props.yAxis ? 55 : 0,
        bottom: 0,
        ...(props.y1Axis ? { right: 55 } : {}),
      },
    },
    scales: {
      x: {
        display: true,
        stacked: props.isStacked,
        grid: {
          display: props.showXGrid,
          color: props.gridBorderColor,
        },
        title: {
          display: true,
          text: '',
          padding: {
            bottom: props.bottomPadding, // Adds space below the X-axis title
          },
        },
        ticks: {
          display: !props.hideXTicks,
          color: '#989CA2',
          font: {
            weight: 'bold',
            size: props.xTickFontSize,
          },
          callback: (value: number | string, index: number) => {
            return props.xAxisTicksCallback?.(value, index) ?? xAxisTransformer(value);
          },
        },

        ...(props.beginXAtValue !== undefined ? { min: props.beginXAtValue } : {}),
        border: {
          display: props.showXAxisBorder,
          ...(props.gridBorderStyle === 'dashed' ? { dash: [5, 5] } : {}),
          color: '#2C2C34',
        },
      },
      y: {
        stacked: props.isStacked,
        grid: {
          display: props.showYGrid,
          color: props.zeroLineColor
            ? (context: ScriptableScaleContext) =>
                context.tick?.value === 0 ? props.zeroLineColor : props.gridBorderColor
            : props.gridBorderColor,
        },
        ...(props.beginYAtValue !== undefined ? { min: props.beginYAtValue } : {}),
        border: {
          ...(props.gridBorderStyle === 'dashed' ? { dash: [5, 5] } : {}),
          color: '#2C2C34',
        },
        ticks: {
          padding: 10,
          color: '#989CA2',
          font: {
            size: props.xTickFontSize,
            weight: 'bold',
          },
          callback: (value: number | string) => {
            const transformed = yAxisTransformer(value);
            const label = transformed.toString();
            return label;
          },
        },
        ...(props.fixedYAxisWidth !== 0
          ? {
              afterFit: (scale: any) => {
                scale.width = props.fixedYAxisWidth;
              },
            }
          : {}),
      },
      ...(props.y1Axis
        ? {
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              grid: {
                display: props.showYGrid,
                color: '#E8EAEB',
              },
              border: {
                dash: [5, 5],
                color: '#2C2C34',
              },
              ticks: {
                padding: 10,
                color: '#989CA2',
                font: {
                  weight: 'bold',
                },
                callback: (value: number | string) => {
                  return y1AxisTransformer(value);
                },
              },
            },
          }
        : {}),
    },
  };
};

export const getDefaultChartPlugins = (args: DefaultChartPluginsProps = {}): Plugin[] => {
  const props: {
    xLabel: string;
    yLabel: string;
    y1Label?: string;
  } = {
    xLabel: 'X Axis',
    yLabel: 'Y Axis',
    ...args,
  };

  return [customXandYAxisLabelPlugin(props.xLabel, props.yLabel, props.y1Label)];
};
